import { useCallback, useRef, useState, type KeyboardEvent, type PointerEvent, type RefObject } from 'react';
import type { MusicVideoEffect } from 'fym-shared/src/music24/musicVideoEffects';
import { moveEffect, setEffectTimes } from '../doc/effectOps';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import type { DragGuide } from './TimelineGuide';
import { beatSec, snapTime, type TimelineView } from './timelineGeometry';

interface UseEffectDragProps {
  laneRef: RefObject<HTMLDivElement | null>;
  view: TimelineView;
  snapOn: boolean;
  onGuide: (guide: DragGuide | null) => void;
}

/** The new place of an effect while it is dragged (the dashed box on the lane). */
export interface EffectDragGhost {
  id: string;
  startSec: number;
  endSec: number;
}

/** A press moves the effect only after the pointer went this far; a shorter move is a click. */
const MOVE_THRESHOLD_PX = 4;

interface EffectDrag {
  id: string;
  /** `move`: the whole effect. `end`: the end edge (the length changes). */
  mode: 'move' | 'end';
  pointerId: number;
  startX: number;
  startSec: number;
  endSec: number;
  moved: boolean;
}

/**
 * Drags an effect on the effects lane, on the pattern of `useClipMove` and `useEdgeDrag`: a drag of
 * the block moves the whole effect and keeps its length; a drag of its end edge changes the length
 * (in the range of the kind). The time snaps to the beat (Alt or snap off: free). One drag is one
 * Undo step. A press with no move stays a click (select the effect); Escape cancels a drag. The
 * arrow keys move a focused block by one beat.
 */
export function useEffectDrag({ laneRef, view, snapOn, onGuide }: UseEffectDragProps) {
  const { change, songMap, select, readOnly } = useMusicVideoEditor();
  const drag = useRef<EffectDrag | null>(null);
  const [ghost, setGhost] = useState<EffectDragGhost | null>(null);
  // The click that the browser sends after a drag must not select again.
  const dragEnded = useRef(false);

  const timeOf = useCallback(
    (current: EffectDrag, clientX: number, altKey: boolean): number | null => {
      const lane = laneRef.current;
      if (lane === null) {
        return null;
      }
      const { width } = lane.getBoundingClientRect();
      if (width <= 0) {
        return null;
      }
      const deltaSec = ((clientX - current.startX) / width) * (view.to - view.from);
      const base = current.mode === 'move' ? current.startSec : current.endSec;
      return snapTime(base + deltaSec, songMap.beats, songMap.bpm, !snapOn || altKey);
    },
    [laneRef, snapOn, songMap.beats, songMap.bpm, view.from, view.to]
  );

  const cancel = useCallback(() => {
    drag.current = null;
    setGhost(null);
    onGuide(null);
  }, [onGuide]);

  const start = useCallback(
    (effect: MusicVideoEffect, mode: EffectDrag['mode'], event: PointerEvent<HTMLElement>) => {
      dragEnded.current = false;
      if (readOnly || event.button !== 0) {
        return;
      }
      if (mode === 'end') {
        event.preventDefault();
        event.stopPropagation();
      }
      event.currentTarget.setPointerCapture(event.pointerId);
      drag.current = {
        id: effect.id,
        mode,
        pointerId: event.pointerId,
        startX: event.clientX,
        startSec: effect.startSec,
        endSec: effect.endSec,
        moved: false,
      };
    },
    [readOnly]
  );

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const { current } = drag;
      if (current === null || current.pointerId !== event.pointerId) {
        return;
      }
      if (!current.moved && Math.abs(event.clientX - current.startX) < MOVE_THRESHOLD_PX) {
        return;
      }
      current.moved = true;
      const sec = timeOf(current, event.clientX, event.altKey);
      if (sec === null) {
        return;
      }
      const lengthSec = current.endSec - current.startSec;
      setGhost(
        current.mode === 'move'
          ? { id: current.id, startSec: sec, endSec: sec + lengthSec }
          : { id: current.id, startSec: current.startSec, endSec: Math.max(current.startSec, sec) }
      );
      onGuide({ sec, free: !snapOn || event.altKey });
    },
    [onGuide, snapOn, timeOf]
  );

  const onPointerUp = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const { current } = drag;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      if (current === null || current.pointerId !== event.pointerId) {
        return;
      }
      cancel();
      if (!current.moved) {
        return;
      }
      dragEnded.current = true;
      const sec = timeOf(current, event.clientX, event.altKey);
      if (sec === null) {
        return;
      }
      change((doc) =>
        current.mode === 'move' ? moveEffect(doc, current.id, sec) : setEffectTimes(doc, current.id, { endSec: sec })
      );
      select({ kind: 'effect', id: current.id });
    },
    [cancel, change, select, timeOf]
  );

  const onKeyDown = useCallback(
    (effect: MusicVideoEffect, event: KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Escape' && drag.current !== null) {
        event.stopPropagation();
        cancel();
        return;
      }
      if (readOnly || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) {
        return;
      }
      event.preventDefault();
      const step = (event.key === 'ArrowLeft' ? -1 : 1) * beatSec(songMap.bpm);
      const sec = snapTime(effect.startSec + step, songMap.beats, songMap.bpm, !snapOn || event.altKey);
      change((doc) => moveEffect(doc, effect.id, sec), `effect:${effect.id}`);
    },
    [cancel, change, readOnly, snapOn, songMap.beats, songMap.bpm]
  );

  /** True once for the click that ends a drag: the lane skips its click action. */
  const takeDragEnd = useCallback((): boolean => {
    const ended = dragEnded.current;
    dragEnded.current = false;
    return ended;
  }, []);

  return {
    ghost,
    onMoveDown: (effect: MusicVideoEffect, event: PointerEvent<HTMLElement>) => start(effect, 'move', event),
    onEndDown: (effect: MusicVideoEffect, event: PointerEvent<HTMLElement>) => start(effect, 'end', event),
    onPointerMove,
    onPointerUp,
    onPointerCancel: cancel,
    onKeyDown,
    takeDragEnd,
  };
}
